import { TOAST_TIMEOUT } from 'src/config/Toast';
import store from 'src/store';
import { createToast } from 'src/store/toastsSlice';
import apiClient from 'src/utils/apiClient';
import { type JmrResult } from 'src/utils/jmrUtils';

import {
  type AccommodationPayload,
  type DownloadOomdPayload,
  type DownloadTWPAPayload,
  type JmrPayload,
  type UpdateAccommodationPayload,
  type UpdateAssociateInfoPayload,
} from './types';

class JmrService {
  /**
   * generate job matches
   */
  public async generateJobMatches(payload: JmrPayload) {
    return await apiClient.post<JmrResult[]>(`jobMatchingReports`, payload);
  }

  /**
   * generate job matches V2 api (restriction jmr)
   */
  public async generateJobMatchesV2(payload: JmrPayload) {
    return await apiClient.post<JmrResult[]>(`jobMatchingReports/v2`, payload);
  }

  /**
   * create accommodation
   */
  public async createAccommodation(payload: AccommodationPayload) {
    const res = await apiClient.post(`/jobMatchingReports/save`, payload);
    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: 'Created accommodation successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * Update restrictions
   * update restrictions
   */
  public async updateAccommodationRestrictions(payload: UpdateAccommodationPayload) {
    const res = await apiClient.put(`/jobMatchingReports/updateNewRestriction`, payload);
    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: 'Created accommodation with updated restrictions successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * Recommend alternative accommodations
   * recommend alternative accommodations
   */
  public async recommendAlternativePlacement(payload: UpdateAccommodationPayload) {
    const res = await apiClient.put(`/jobMatchingReports/updatePlacement`, payload);
    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: 'Placement job selection(s) updated successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * Complete Transfer Placement
   * complete Transfer Placement
   */
  public async completeTransferPlacement(payload: UpdateAccommodationPayload) {
    const res = await apiClient.put(`/jobMatchingReports/completeTransferPlacement`, payload);
    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: 'Placement has been transferred successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * Update RestrictionDates
   */
  public async updateRestrictionDates(payload: UpdateAccommodationPayload) {
    const res = await apiClient.put(`/jobMatchingReports/updateRestrictionDates`, payload);
    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: 'Placement restrictions dates updated successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * Provider Placed Off Work
   */
  public async providerPlacedOffWork(payload: UpdateAccommodationPayload) {
    const res = await apiClient.post(`/jobMatchingReports/providerPlacedOffWork`, payload);
    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: 'Provider Placed Off Work Successful',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * Update Associate Info
   */
  public async updateAssociateInfo(payload: UpdateAssociateInfoPayload) {
    const res = await apiClient.put(`/jobMatchingReports/associateInfo`, payload);
    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: 'Associate information updated successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * Get accommodation detail
   * get accommodation detail
   */
  public async getAccommodationDetail(accommodationId: number) {
    const res = await apiClient.get(`/accommodation/detail/${accommodationId}`);
    return res;
  }

  /**
   * Get accommodation detail base on austin case and user login
   * get accommodation detail base on austin case and user login
   */
  public async getAccommodationIdByCaseIdAndLogin(type: string, caseId: string, login: string) {
    const res = await apiClient.get(`/accommodation/type/${type}/caseId/${caseId}/login/${login}`);
    return res;
  }

  /**
   * Get accommodation
   * get accommodation
   */
  public async getAccommodation(accommodationId: number) {
    const res = await apiClient.get(`/accommodation/${accommodationId}`);
    return res;
  }

  /**
   * Download TWPA
   * download TWPA
   */
  public async downloadTWPA(payload: DownloadTWPAPayload) {
    const res = await apiClient.post(`/accommodation/downloadTWPA`, payload, {
      responseType: 'blob',
    });
    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: 'Downloaded TWPA successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * Download OOMD
   * download OOMD
   */
  public async downloadOOMD(payload: DownloadOomdPayload) {
    const res = await apiClient.post(`/accommodation/downloadOOMD`, payload, {
      responseType: 'blob',
    });
    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: 'Downloaded OOMD successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * Download JMR
   * download JMR
   */
  public async downloadJMR(key: string) {
    const res = await apiClient.get(`/accommodation/downloadJMR/${key}`, {
      responseType: 'blob',
    });
    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: 'Downloaded JMR successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * Delete accommodation
   * delete accommodation
   */
  public async deleteAccommodation(accommodationId: number) {
    const res = await apiClient.delete(`/accommodation/${accommodationId}`);
    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: 'Deleted accommodation successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * Get Updat eRestriction Allowed
   */
  public async getUpdateRestrictionAllowed(accommodationId: number) {
    const res = await apiClient.get<boolean>(`/accommodation/${accommodationId}/updateRestrictionAllowed`);
    return res;
  }

  /**
   * Get Bcc Email by Id
   */
  public async getBccEmailById(placementId: number, emailType: string) {
    const res = await apiClient.get(`accommodation/${placementId}/emailBccRecipients/emailType/${emailType}`);
    return res;
  }
}

export default new JmrService();
